import React, { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

/**
 * Thin elegant wavy lines connecting designer to wearer,
 * like threads flowing between two figures.
 * Smooth quadratic curves, subtle cursor response.
 */
const LINES = [
  { yOff: -0.08, freq: 1.2, amp: 0.03, phase: 0,    color: 'rgba(26,188,176,0.4)',  width: 1.3 },
  { yOff: -0.03, freq: 1.5, amp: 0.025, phase: 1.0, color: 'rgba(26,188,176,0.22)', width: 0.8 },
  { yOff:  0.01, freq: 0.9, amp: 0.04, phase: 2.0,  color: 'rgba(26,26,26,0.10)',   width: 1.0 },
  { yOff:  0.05, freq: 1.3, amp: 0.028, phase: 3.0, color: 'rgba(232,105,42,0.35)', width: 1.2 },
  { yOff:  0.10, freq: 1.1, amp: 0.035, phase: 4.0, color: 'rgba(232,105,42,0.18)', width: 0.7 },
];

const HeroSection = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const dImgRef = useRef(null);
  const wImgRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });
  const raf = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const cEl = containerRef.current;
    const dImg = dImgRef.current;
    const wImg = wImgRef.current;
    if (!canvas || !cEl || !dImg || !wImg) {
      raf.current = requestAnimationFrame(draw);
      return;
    }

    const cR = cEl.getBoundingClientRect();
    const dR = dImg.getBoundingClientRect();
    const wR = wImg.getBoundingClientRect();
    const W = cR.width;
    const H = cR.height;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    // Skip on mobile
    const hGap = wR.left - dR.right;
    if (hGap < W * 0.15) {
      raf.current = requestAnimationFrame(draw);
      return;
    }

    const m = mouse.current;

    // Lines flow from designer's center-right to wearer's center-left
    const startX = (dR.left + dR.right) / 2 - cR.left + dR.width * 0.15;
    const endX = (wR.left + wR.right) / 2 - cR.left - wR.width * 0.15;
    const midY = H * 0.5;

    const mouseInfluence = (m.x - 0.5) * 1.5 + (m.y - 0.5) * 0.8;

    LINES.forEach(({ yOff, freq, amp, phase, color, width }) => {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Build points first
      const STEPS = 60;
      const pts = [];
      for (let i = 0; i <= STEPS; i++) {
        const t = i / STEPS;
        const x = startX + (endX - startX) * t;
        const edgeFade = Math.sin(t * Math.PI);
        const wave = Math.sin(t * Math.PI * 2 * freq + phase + mouseInfluence) * amp * H * edgeFade;
        pts.push({ x, y: midY + yOff * H + wave });
      }

      // Draw smooth quadratic curve through points
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i].x + pts[i + 1].x) / 2;
        const my = (pts[i].y + pts[i + 1].y) / 2;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
      }
      ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
      ctx.stroke();
    });

    raf.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const onMove = (cx, cy) => {
      const c = containerRef.current;
      if (!c) return;
      const r = c.getBoundingClientRect();
      mouse.current = {
        x: Math.max(0, Math.min(1, (cx - r.left) / r.width)),
        y: Math.max(0, Math.min(1, (cy - r.top) / r.height)),
      };
    };
    const onMouse = (e) => onMove(e.clientX, e.clientY);
    const onTouch = (e) => {
      if (e.touches[0]) onMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    window.addEventListener('mousemove', onMouse);
    window.addEventListener('touchmove', onTouch, { passive: true });
    raf.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('touchmove', onTouch);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [draw]);

  return (
    <section className="landing-hero" ref={containerRef}>
      <canvas ref={canvasRef} className="hero-connections-canvas" />

      <div className="hero-panel hero-panel-designer">
        <img
          ref={dImgRef}
          src="/designer-illustration.svg"
          alt="Fashion designer at work"
          className="hero-panel-img"
          draggable={false}
        />
        <div className="hero-panel-text">
          <span className="hero-label">For Designers</span>
          <h2 className="hero-heading">Your designs,<br/>infinite reach</h2>
          <p className="hero-subtext">License, rent, or sell — with AI virtual try-on built in.</p>
          <div className="hero-cta hero-cta-group">
            <Link to="/listing/upload" className="landing-btn-primary hero-cta-teal">
              Start Listing
            </Link>
            <Link to="/browse" className="landing-btn-primary hero-cta-mobile">
              Explore Designs
            </Link>
          </div>
        </div>
      </div>

      <div className="hero-panel hero-panel-wearer">
        <img
          ref={wImgRef}
          src="/wearer-illustration.svg"
          alt="Experiencing fashion design"
          className="hero-panel-img"
          draggable={false}
        />
        <div className="hero-panel-text">
          <span className="hero-label">For Fashion Lovers</span>
          <h2 className="hero-heading">Try it on.<br/>Rent, buy, or<br/>custom order.</h2>
          <p className="hero-subtext">AI try-on for any piece. Rent couture, buy favorites, or request made-to-order.</p>
          <Link to="/browse" className="landing-btn-primary hero-cta hero-cta-desktop">
            Explore Designs
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
